// Trading Mind Layer — Pure scoring & detection functions
import { READINESS_WEIGHTS, SCORE_THRESHOLDS } from '../config/mindConstants';

// Normalize a 1-5 slider value to 0-100
function normalize(val) {
  return ((Math.max(1, Math.min(5, val)) - 1) / 4) * 100;
}

// Readiness Score (0-100) from daily check-in values
export function computeReadinessScore({ mood, focus, stress, sleep }) {
  return Math.round(
    READINESS_WEIGHTS.mood * normalize(mood) +
    READINESS_WEIGHTS.focus * normalize(focus) +
    READINESS_WEIGHTS.stress * normalize(stress) +
    READINESS_WEIGHTS.sleep * normalize(sleep)
  );
}

// Energy Score (0-100), dynamic — drops throughout the day based on performance
export function computeEnergyScore(readiness, performance, settings) {
  const { consecutiveLosses = 0, tradesToday = 0, dailyPnL = 0 } = performance;
  const { maxTradesPerDay = 10, dailyLossLimit = 5000 } = settings;

  const tradeFatigue = Math.max(0, tradesToday - Math.floor(maxTradesPerDay * 0.6));
  const lossRatio = dailyPnL < 0 ? Math.abs(dailyPnL) / dailyLossLimit : 0;

  const energy = readiness
    - (consecutiveLosses * 8)
    - (tradeFatigue * 3)
    - (lossRatio * 25);

  return Math.round(Math.max(0, Math.min(100, energy)));
}

// Readiness level label & color
export function getReadinessLevel(score) {
  const { readiness } = SCORE_THRESHOLDS;
  if (score >= readiness.excellent.min) return readiness.excellent;
  if (score >= readiness.good.min)      return readiness.good;
  if (score >= readiness.caution.min)   return readiness.caution;
  return readiness.poor;
}

// Energy level label & color
export function getEnergyLevel(score) {
  const { energy } = SCORE_THRESHOLDS;
  if (score >= energy.high.min)   return energy.high;
  if (score >= energy.medium.min) return energy.medium;
  return energy.low;
}

// Detect revenge-trade pattern: same instrument within 60s of a loss
export function detectRevengePattern(orderData, recentTrades) {
  if (!recentTrades || recentTrades.length === 0) return false;
  const now = Date.now();
  const symbol = orderData.tradingsymbol || orderData.symboltoken;

  return recentTrades.some(trade => {
    const sameInstrument = (trade.tradingsymbol || trade.symboltoken) === symbol;
    const wasLoss = (trade.pnl || 0) < 0;
    const withinWindow = (now - (trade.timestamp || 0)) < 60000; // 60 seconds
    return sameInstrument && wasLoss && withinWindow;
  });
}

// Date helpers
export function getTodayStr() {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

export function getWeekId(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}
